import { getApiBaseUrl } from '@/shared/config/runtime-config';

interface IVendorData {
    session_id?: string;
    session_token?: string;
    interview_id?: string;
    transaction_id?: string;
    token?: string;
    interview_token?: string;
    authentication?: Record<string, any>;
    region?: string;
    show_coaching?: boolean;
    api_url?: string;
    liveness_session_id?: string;
    candidate?: string;
    error?: boolean;
    error_status?: string | number | null;
    error_message?: string;
}

interface ITokenResponse {
    status?: string;
    result?: string;
    transaction_id?: string;
    error_status?: number;
    error_message?: string;
    vendor_response?: Record<string, any>;
    error?: string;
    trace_id?: string;
}

interface IPersonAdapter {
    fetchToken(vendor: string, livenessSessionId: string, vendorData: IVendorData): Promise<ITokenResponse>;
    fetchTokenByDevice(personUuid: string, deviceId: string, livenessSessionId: string): Promise<ITokenResponse | null>;
    fetchLivenessSession(livenessSessionId: string): Promise<Record<string, any>>;
}

class PersonAdapter implements IPersonAdapter {
    private apiBaseUrl: string;

    constructor() {
        const rawApiBaseUrl = getApiBaseUrl();
        if (!rawApiBaseUrl) {
            throw new Error("Liveness API base URL is not configured.");
        }
        this.apiBaseUrl = rawApiBaseUrl;
    }

    private validateTokenResponse(response: any): ITokenResponse {
        if (!response || typeof response !== 'object') {
            throw new Error('Invalid token response: expected an object');
        }
        // Handle both direct response and data-wrapped response
        const data = response.data || response;
        return {
            status: data.status,
            result: data.result,
            transaction_id: data.transaction_id,
            error_status: data.error_status,
            error_message: data.error_message,
            vendor_response: data.vendor_response,
            error: data.error,
            trace_id: data.trace_id,
        } as ITokenResponse;
    }

    private validateSessionResponse(response: any): Record<string, any> {
        if (!response || typeof response !== 'object') {
            throw new Error('Invalid session response: expected an object');
        }
        return response as Record<string, any>;
    }

    // Adapter logic here
    async fetchToken(vendor: string, livenessSessionId: string, vendorData: IVendorData): Promise<ITokenResponse> {
        if (vendor === "incode" || vendor === "incode-eu" || vendor === "aws") {

            const res = await fetch(`${this.apiBaseUrl}/persons/api/liveness/${livenessSessionId}`, {
                method: "POST",
                redirect: "follow",
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(vendorData)
            });
            
            if (!res.ok) {
                throw new Error(`HTTP error! status: ${res.status}`);
            }
            
            const response = await res.json();
            return this.validateTokenResponse(response);

        }
        throw new Error(`Unsupported vendor: ${vendor}`);
    }

    async fetchTokenByDevice(personUuid: string, deviceId: string, livenessSessionId: string): Promise<ITokenResponse | null> {
        try {
            const res = await fetch(`${this.apiBaseUrl}/persons/api/liveness/${livenessSessionId}/source`, {
                method: "POST",
                redirect: "follow",
                body: JSON.stringify({ source: deviceId, person_uuid: personUuid }),
                headers: { 'Content-Type': 'application/json' }
            });
            
            if (!res.ok) {
                throw new Error(`HTTP error! status: ${res.status}`);
            }
            
            const response = await res.json();
            const tokenResponse = this.validateTokenResponse(response);

            // Backend returns { status: "PENDING" } when no token is available yet
            if (tokenResponse.status === 'PENDING') {
                throw new Error('Polling timeout');
            }

            return tokenResponse;
        } catch (error) {
            // Don't log errors for polling timeouts
            if (error instanceof Error && error.message === 'Polling timeout') {
                return Promise.reject(error);
            }
            return Promise.reject(error);
        }
        
    }

    async fetchLivenessSession(livenessSessionId: string): Promise<Record<string, any>> {
        const res = await fetch(`${this.apiBaseUrl}/persons/api/liveness/${livenessSessionId}`, {
            method: "GET",
            redirect: "follow"
        });
        
        if (!res.ok) {
            throw new Error(`Failed to fetch liveness session: ${res.status} ${res.statusText}`);
        }
        
        const response = await res.json();
        return this.validateSessionResponse(response);
    }
}

let personAdapter: PersonAdapter | null = null;

function getPersonAdapter(): PersonAdapter {
    if (!personAdapter) {
        personAdapter = new PersonAdapter();
    }
    return personAdapter;
}

export { getPersonAdapter, PersonAdapter };
export type { IPersonAdapter, IVendorData, ITokenResponse };
